// Permission checking utilities with dynamic role support

// Resource categories for UI grouping
export const RESOURCE_CATEGORIES = {
  DATA_ENTRY: {
    label: 'Data Entry Screens',
    resources: ['customers', 'contacts', 'employees', 'salaries', 'revenues', 'allocations']
  },
  COMPANY_MASTERS: {
    label: 'Company Masters',
    resources: ['vertical_master', 'product_master', 'uom_master', 'item_group_master', 'items_master', 'currency_master', 'payment_splits_master']
  },
  CLOUD: {
    label: 'Cloud Module',
    resources: ['cloud_master', 'server_type_master', 'server_master', 'server_costs', 'server_allocations', 'cloud_revenues', 'server_clusters']
  },
  PROJECT_MANAGEMENT: {
    label: 'Project Management',
    resources: ['module_utilization', 'client_dashboard', 'project_master', 'task_type_master', 'task_status_master', 'client_meetings', 'client_trainings', 'review_dashboard', 'client_contact_dashboard']
  },
  COMMERCIAL: {
    label: 'Commercial',
    resources: ['commercial_proposals', 'commercial_contracts', 'commercial_purchase_orders', 'commercial_invoices', 'commercial_pricing_rules', 'commercial_dashboard']
  },
  REPORTS: {
    label: 'Reports',
    resources: ['manpower_report', 'client_report', 'unallocated_report', 'managers_report', 'cost_breakdown_report', 'manpower_allocation_history', 'manpower_revenue_history', 'cloud_client_report', 'cloud_product_report', 'cloud_server_report', 'cloud_allocation_history', 'cloud_revenue_history', 'advanced_reports', 'contact_list_report', 'client_engagement_report', 'contact_coverage_report']
  },
  DASHBOARD: {
    label: 'Dashboard',
    resources: ['system_overview', 'cockpit_activity', 'dashboard', 'cloud_dashboard']
  },
  ADMIN: {
    label: 'Configuration',
    resources: ['user_management', 'role_management', 'integrations', 'email_configuration', 'notification_settings', 'audit_logs', 'data_hygiene']
  }
};

// Resource labels for display
export const RESOURCE_LABELS = {
  customers: 'Customer Master',
  contacts: 'Contact Master',
  employees: 'Employee Master',
  salaries: 'Salaries',
  revenues: 'Revenues',
  allocations: 'Allocations',
  company_masters: 'Company Masters (all)',  // kept for backward compat
  vertical_master: 'Vertical Master',
  product_master: 'Product Master',
  uom_master: 'Unit of Measure (UoM)',
  item_group_master: 'Item Group Master',
  items_master: 'Items Master',
  currency_master: 'Currencies',
  payment_splits_master: 'Payment Splits',
  server_type_master: 'Server Type Master',
  server_master: 'Server Master',
  server_costs: 'Server Costs',
  server_allocations: 'Server Allocations',
  cloud_revenues: 'Cloud Revenues',
  server_clusters: 'Server Clusters',
  cloud_master: 'Cloud Infrastructure Master',
  module_utilization: 'Module Utilization',
  client_dashboard: 'Client Profile & Dashboard',
  project_master: 'Project Master, Plan & Reviews',
  task_type_master: 'Task Type Master',
  task_status_master: 'Task Status Master',
  client_meetings: 'Client Meetings',
  client_trainings: 'Client Trainings',
  review_dashboard: 'Review Dashboard',
  client_contact_dashboard: 'Contact Coverage Dashboard',
  manpower_report: 'Manpower Report',
  client_report: 'Client Report',
  unallocated_report: 'Unallocated Report',
  managers_report: 'Managers Report',
  cost_breakdown_report: 'Cost Breakdown',
  manpower_allocation_history: 'Manpower Allocation History',
  manpower_revenue_history: 'Manpower Revenue History',
  cloud_allocation_history: 'Cloud Allocation History',
  cloud_revenue_history: 'Cloud Revenue History',
  cloud_client_report: 'Cloud Client Report',
  cloud_product_report: 'Cloud Product Report',
  cloud_server_report: 'Cloud Server Report',
  advanced_reports: 'Advanced Reports (All 11)',
  contact_list_report: 'Contact List Report',
  client_engagement_report: 'Client Engagement Report',
  contact_coverage_report: 'Contact Coverage Report',
  dashboard: 'Manpower Dashboard',
  cloud_dashboard: 'Cloud Dashboard',
  user_management: 'User Management',
  role_management: 'Role Management',
  email_configuration: 'Email Configuration',
  integrations: 'Integrations',
  notification_settings: 'Notification Engine',
  audit_logs: 'Audit Logs',
  data_hygiene: 'Data Hygiene',
  commercial_proposals: 'Proposals',
  commercial_contracts: 'Contracts',
  commercial_purchase_orders: 'Purchase Orders',
  commercial_invoices: 'Invoices',
  commercial_pricing_rules: 'Pricing Rules',
  commercial_dashboard: 'Commercial Dashboard',
  system_overview: 'System Overview',
  cockpit_activity: 'Cockpit Activity',
};

// Action types per resource category
export const DATA_ENTRY_ACTIONS = ['view', 'create', 'edit', 'delete'];
export const CLOUD_ACTIONS = ['view', 'create', 'edit', 'delete'];
export const PROJECT_MANAGEMENT_ACTIONS = ['view', 'create', 'edit', 'delete', 'export', 'import'];
export const PROJECT_DASHBOARD_ACTIONS = ['view'];
export const REPORT_ACTIONS = ['view', 'export'];
export const DASHBOARD_ACTIONS = ['view'];
export const ADMIN_ACTIONS = ['view', 'create', 'edit', 'delete'];
export const EMAIL_CONFIG_ACTIONS = ['view', 'edit'];
export const AUDIT_LOG_ACTIONS = ['view', 'export'];
export const DATA_HYGIENE_ACTIONS = ['view', 'delete'];

export const COMMERCIAL_ACTIONS = ['view', 'create', 'edit', 'delete'];

// Get actions for a resource
export const getActionsForResource = (resource) => {
  if (RESOURCE_CATEGORIES.COMMERCIAL.resources.includes(resource)) {
    if (resource === 'commercial_dashboard') return DASHBOARD_ACTIONS;
    return COMMERCIAL_ACTIONS;
  }
  if (RESOURCE_CATEGORIES.DATA_ENTRY.resources.includes(resource)) {
    return DATA_ENTRY_ACTIONS;
  }
  if (RESOURCE_CATEGORIES.COMPANY_MASTERS?.resources?.includes(resource)) {
    return DATA_ENTRY_ACTIONS;
  }
  if (resource === 'company_masters') {
    return DATA_ENTRY_ACTIONS;  // backward compat
  }
  if (RESOURCE_CATEGORIES.CLOUD.resources.includes(resource)) {
    return CLOUD_ACTIONS;
  }
  if (resource === 'module_utilization') {
    return PROJECT_MANAGEMENT_ACTIONS;
  }
  if (['client_dashboard', 'review_dashboard', 'client_contact_dashboard', 'system_overview', 'cockpit_activity'].includes(resource)) {
    return PROJECT_DASHBOARD_ACTIONS;
  }
  if (resource === 'client_meetings') {
    return DATA_ENTRY_ACTIONS;
  }
  if (resource === 'client_trainings') {
    return DATA_ENTRY_ACTIONS;
  }
  if (resource === 'email_configuration') {
    return EMAIL_CONFIG_ACTIONS;
  }
  if (resource === 'integrations') {
    return EMAIL_CONFIG_ACTIONS;
  }
  if (resource === 'notification_settings') {
    return EMAIL_CONFIG_ACTIONS;
  }
  if (resource === 'audit_logs') {
    return AUDIT_LOG_ACTIONS;
  }
  if (resource === 'data_hygiene') {
    return DATA_HYGIENE_ACTIONS;
  }
  if (resource === 'project_master') {
    return DATA_ENTRY_ACTIONS;
  }
  if (RESOURCE_CATEGORIES.REPORTS.resources.includes(resource)) {
    return REPORT_ACTIONS;
  }
  if (RESOURCE_CATEGORIES.DASHBOARD.resources.includes(resource)) {
    return DASHBOARD_ACTIONS;
  }
  if (RESOURCE_CATEGORIES.ADMIN.resources.includes(resource)) {
    return ADMIN_ACTIONS;
  }
  return [];
};

// Legacy role labels (for backward compatibility)
export const ROLE_LABELS = {
  admin: 'Admin',
  finance_executive: 'Finance Executive',
  finance_manager: 'Finance Manager',
  manpower_executive: 'Manpower Executive',
  manpower_manager: 'Manpower Manager',
  hr_manager: 'HR Manager',
};

// Default permission structure for new roles
export const getDefaultPermissions = () => ({
  customers: { view: false, create: false, edit: false, delete: false },
  contacts: { view: false, create: false, edit: false, delete: false },
  employees: { view: false, create: false, edit: false, delete: false },
  salaries: { view: false, create: false, edit: false, delete: false },
  revenues: { view: false, create: false, edit: false, delete: false },
  allocations: { view: false, create: false, edit: false, delete: false },
  company_masters: { view: false, create: false, edit: false, delete: false },  // backward compat
  vertical_master: { view: false, create: false, edit: false, delete: false },
  product_master: { view: false, create: false, edit: false, delete: false },
  uom_master: { view: false, create: false, edit: false, delete: false },
  item_group_master: { view: false, create: false, edit: false, delete: false },
  items_master: { view: false, create: false, edit: false, delete: false },
  currency_master: { view: false, create: false, edit: false, delete: false },
  payment_splits_master: { view: false, create: false, edit: false, delete: false },
  server_type_master: { view: false, create: false, edit: false, delete: false },
  server_master: { view: false, create: false, edit: false, delete: false },
  server_costs: { view: false, create: false, edit: false, delete: false },
  server_allocations: { view: false, create: false, edit: false, delete: false },
  cloud_revenues: { view: false, create: false, edit: false, delete: false },
  server_clusters: { view: false, create: false, edit: false, delete: false },
  module_utilization: { view: false, create: false, edit: false, delete: false, export: false, import: false },
  client_dashboard: { view: false },
  project_master: { view: false, create: false, edit: false, delete: false },
  client_meetings: { view: false, create: false, edit: false, delete: false },
  client_trainings: { view: false, create: false, edit: false, delete: false },
  review_dashboard: { view: false },
  client_contact_dashboard: { view: false },
  manpower_report: { view: false, export: false },
  client_report: { view: false, export: false },
  unallocated_report: { view: false, export: false },
  managers_report: { view: false, export: false },
  cost_breakdown_report: { view: false, export: false },
  manpower_allocation_history: { view: false, export: false },
  manpower_revenue_history: { view: false, export: false },
  cloud_allocation_history: { view: false, export: false },
  cloud_revenue_history: { view: false, export: false },
  cloud_client_report: { view: false, export: false },
  cloud_product_report: { view: false, export: false },
  cloud_server_report: { view: false, export: false },
  advanced_reports: { view: false, export: false },
  contact_list_report: { view: false, export: false },
  client_engagement_report: { view: false, export: false },
  contact_coverage_report: { view: false, export: false },
  commercial_proposals: { view: false, create: false, edit: false, delete: false },
  commercial_contracts: { view: false, create: false, edit: false, delete: false },
  commercial_purchase_orders: { view: false, create: false, edit: false, delete: false },
  commercial_invoices: { view: false, create: false, edit: false, delete: false },
  commercial_pricing_rules: { view: false, create: false, edit: false, delete: false },
  commercial_dashboard: { view: false },
  dashboard: { view: false },
  cloud_dashboard: { view: false },
  system_overview: { view: false },
  cockpit_activity: { view: false },
  user_management: { view: false, create: false, edit: false, delete: false },
  role_management: { view: false, create: false, edit: false, delete: false },
  email_configuration: { view: false, edit: false },
  integrations: { view: false, edit: false },
  notification_settings: { view: false, edit: false },
  audit_logs: { view: false, export: false },
  data_hygiene: { view: false, delete: false },
});

// Check if user has permission (local check from stored permissions)
export const checkPermission = (userPermissions, resource, action) => {
  if (!userPermissions) return false;
  const resourcePerms = userPermissions[resource];
  if (!resourcePerms) return false;
  return resourcePerms[action] === true;
};

// Check if user has view permission for a menu item
export const canViewMenu = (userPermissions, resource) => {
  return checkPermission(userPermissions, resource, 'view');
};

// Map route to resource for menu visibility
export const ROUTE_TO_RESOURCE = {
  '/dashboard': 'dashboard',
  '/cloud/dashboard': 'cloud_dashboard',
  '/customers': 'customers',
  '/contacts': 'contacts',
  '/employees': 'employees',
  '/salaries': 'salaries',
  '/salaries/bulk-edit': 'salaries',
  '/revenues': 'revenues',
  '/revenues/bulk-edit': 'revenues',
  '/allocations': 'allocations',
  '/allocations/bulk-edit': 'allocations',
  '/cloud/clouds': 'cloud_master',
  '/cloud/server-types': 'server_type_master',
  '/cloud/servers': 'server_master',
  '/cloud/server-costs': 'server_costs',
  '/cloud/server-allocations': 'server_allocations',
  '/cloud/revenues': 'cloud_revenues',
  '/cloud/server-clusters': 'server_clusters',
  '/cloud/reports/client': 'cloud_client_report',
  '/cloud/reports/product': 'cloud_product_report',
  '/cloud/reports/server': 'cloud_server_report',
  '/cloud/reports/unallocated': 'cloud_server_report',
  '/project/module-utilization': 'module_utilization',
  '/project/client-dashboard': 'client_dashboard',
  '/project/task-types': 'task_type_master',
  '/project/task-statuses': 'task_status_master',
  '/project/projects': 'project_master',
  '/project/plan': 'project_master',
  '/project/meetings': 'client_meetings',
  '/project/meeting-intelligence': 'client_meetings',
  '/project/trainings': 'client_trainings',
  '/project/reviews': 'project_master',
  '/project/contact-coverage': 'client_contact_dashboard',
  '/reports/manpower': 'manpower_report',
  '/reports/client': 'client_report',
  '/reports/product': 'manpower_report',
  '/reports/unallocated': 'unallocated_report',
  '/reports/managers': 'managers_report',
  '/reports/cost-breakdown': 'cost_breakdown_report',
  '/reports/manpower-allocation-history': 'manpower_allocation_history',
  '/reports/manpower-revenue-history': 'manpower_revenue_history',
  '/cloud/reports/allocation-history': 'cloud_allocation_history',
  '/cloud/reports/revenue-history': 'cloud_revenue_history',
  '/allocations/manage': 'allocations',
  '/revenues/manage': 'revenues',
  '/cloud/allocations/manage': 'server_allocations',
  '/cloud/revenues/manage': 'cloud_revenues',
  '/reports/revenue': 'advanced_reports',
  '/reports/win-rate': 'advanced_reports',
  '/reports/invoice-aging': 'advanced_reports',
  '/reports/po-utilization': 'advanced_reports',
  '/reports/expiry-calendar': 'advanced_reports',
  '/reports/profitability': 'advanced_reports',
  '/reports/employee-utilization': 'advanced_reports',
  '/reports/project-health': 'advanced_reports',
  '/reports/contact-coverage': 'contact_coverage_report',
  '/reports/client-engagement': 'client_engagement_report',
  '/reports/contact-list': 'contact_list_report',
  '/reports/server-cost': 'advanced_reports',
  '/reports/cloud-spend': 'advanced_reports',
  '/verticals': 'vertical_master',
  '/masters/uom': 'uom_master',
  '/masters/item-groups': 'item_group_master',
  '/masters/items': 'items_master',
  '/masters/currencies': 'currency_master',
  '/masters/payment-splits': 'payment_splits_master',
  '/products': 'product_master',
  '/users': 'user_management',
  '/roles': 'role_management',
  '/email-config': 'integrations',
  '/integrations': 'integrations',
  '/notification-settings': 'notification_settings',
  '/audit-logs': 'audit_logs',
  '/data-hygiene': 'data_hygiene',
  '/dashboards/overview': 'system_overview',
  '/dashboards/activity': 'cockpit_activity',
  '/commercial/dashboard': 'commercial_dashboard',
  '/commercial/proposals': 'commercial_proposals',
  '/commercial/contracts': 'commercial_contracts',
  '/commercial/purchase-orders': 'commercial_purchase_orders',
  '/commercial/invoices': 'commercial_invoices',
  '/commercial/pricing-rules': 'commercial_pricing_rules',
};
